import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import conceptsService from '../services/concepts.service';
import rubrosPresupuestalesService from '../services/rubros-presupuestales.service';
import { RubroPresupuestal } from '../types';

const ConceptoForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [rubros, setRubros] = useState<RubroPresupuestal[]>([]);
  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    descripcion: '',
    precioLista: '',
    rubroPresupuestalId: '',
    activo: true,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Cargar rubros activos
        const rubrosData = await rubrosPresupuestalesService.getRubrosActivos();
        setRubros(rubrosData || []);

        // Si estamos editando, cargar el concepto
        if (isEditing && id) {
          const concepto = await conceptsService.getConceptoById(parseInt(id));
          setFormData({
            codigo: concepto.codigo || '',
            nombre: concepto.nombre,
            descripcion: concepto.descripcion || '',
            precioLista: concepto.precioLista.toString(),
            rubroPresupuestalId: concepto.rubroPresupuestalId?.toString() || '',
            activo: concepto.activo,
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        alert('Error al cargar los datos');
        if (isEditing) {
          navigate('/conceptos');
        }
      }
    };
    fetchData();
  }, [id, isEditing, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handlePrecioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setFormData(prev => ({ ...prev, precioLista: value }));
  };

  const formatPrecio = (value: string) => {
    if (!value) return '';
    return parseInt(value).toLocaleString('es-CO');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const nombre = (formData.nombre || '').trim();
      const codigo = (formData.codigo || '').trim();
      const descripcion = (formData.descripcion || '').trim();
      const precioLista = Number((formData.precioLista || '').trim());

      if (!nombre) {
        alert('El nombre es requerido');
        return;
      }
      if (!Number.isFinite(precioLista) || precioLista <= 0) {
        alert('El precio de lista debe ser un número mayor a 0');
        return;
      }

      const submitData = {
        codigo: codigo || undefined,
        nombre,
        descripcion: descripcion || undefined,
        precioLista,
        rubroPresupuestalId: formData.rubroPresupuestalId ? parseInt(formData.rubroPresupuestalId) : undefined,
        activo: formData.activo,
      };

      if (isEditing && id) {
        await conceptsService.updateConcepto(parseInt(id), submitData);
      } else {
        await conceptsService.createConcepto(submitData as any);
      }
      navigate('/conceptos');
    } catch (error: any) {
      console.error('Error saving concepto:', error);
      const msg = error?.response?.data?.message || error?.message || 'Error al guardar el concepto';
      alert(Array.isArray(msg) ? msg.join('\n') : msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/conceptos')}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Volver a conceptos
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditing ? 'Editar Concepto' : 'Nuevo Concepto'}
        </h1>
      </div>

      {/* Form */}
      <div className="bg-white shadow sm:rounded-lg">
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {/* Código */}
            <div>
              <label htmlFor="codigo" className="block text-sm font-medium text-gray-700 mb-2">
                Código
              </label>
              <input
                type="text"
                id="codigo"
                name="codigo"
                value={formData.codigo}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Ej: SRV-001"
              />
              <p className="mt-1 text-sm text-gray-500">Opcional. Debe ser único.</p>
            </div>

            {/* Nombre */}
            <div>
              <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-2">
                Nombre *
              </label>
              <input
                type="text"
                id="nombre"
                name="nombre"
                required
                value={formData.nombre}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Ej: Consultoría en TI"
              />
            </div>

            {/* Precio Lista */}
            <div>
              <label htmlFor="precioLista" className="block text-sm font-medium text-gray-700 mb-2">
                Precio Lista *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500">$</span>
                <input
                  type="text"
                  id="precioLista"
                  name="precioLista"
                  required
                  value={formatPrecio(formData.precioLista)}
                  onChange={handlePrecioChange}
                  className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-right"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Rubro Presupuestal */}
            <div>
              <label htmlFor="rubroPresupuestalId" className="block text-sm font-medium text-gray-700 mb-2">
                Rubro Presupuestal
              </label>
              <select
                id="rubroPresupuestalId"
                name="rubroPresupuestalId"
                value={formData.rubroPresupuestalId}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Sin rubro asignado</option>
                {rubros.map((rubro) => (
                  <option key={rubro.id} value={rubro.id}>
                    {rubro.codigo ? `${rubro.codigo} - ` : ''}{rubro.nombre} ({rubro.tipo})
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Opcional. Permite análisis presupuestal por categoría.
              </p>
            </div>

            {/* Estado */}
            <div>
              <label htmlFor="activo" className="block text-sm font-medium text-gray-700 mb-2">
                Estado
              </label>
              <select
                id="activo"
                name="activo"
                value={formData.activo.toString()}
                onChange={(e) => setFormData(prev => ({ ...prev, activo: e.target.value === 'true' }))}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="true">Activo</option>
                <option value="false">Inactivo</option>
              </select>
            </div>

            {/* Descripción */}
            <div className="sm:col-span-2">
              <label htmlFor="descripcion" className="block text-sm font-medium text-gray-700 mb-2">
                Descripción
              </label>
              <textarea
                id="descripcion"
                name="descripcion"
                rows={4}
                value={formData.descripcion}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Descripción detallada del servicio..."
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/conceptos')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear Concepto')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConceptoForm;
